/**
 * One animation loop for the orb: eases the state weights, keeps a phase clock and a level (live audio
 * when given, else each state's own energy), and calls onFrame. It stops when the orb is off screen or
 * the page hidden, and holds still with reduced motion.
 * From the "Particles Orb" component (lib/use-orb-animator.ts, use-in-view.ts, use-reduced-motion.ts).
 */
import { useEffect, useRef, type RefObject } from 'react';
import { approach, blendEnergy, createStateMix, type OrbState, type StateWeights } from './orb-state';

const MAX_FRAME_DT = 0.1;
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

export interface OrbFrame {
  dt: number;
  phase: number;
  weights: StateWeights;
  level: number;
  reduced: boolean;
}

/** The level rises fast and falls slowly, like a VU meter. */
export const smoothLevel = (current: number, target: number, dt: number): number =>
  approach(current, target, target > current ? 14 : 4, dt);

/** Calls onChange(false) when the element leaves the screen or the page is hidden, (true) when back. */
function observeActivity(el: Element, onChange: (active: boolean) => void): () => void {
  let inView = true;
  let visible = document.visibilityState === 'visible';
  let active = inView && visible;
  const sync = () => {
    const next = inView && visible;
    if (next !== active) onChange((active = next));
  };
  const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver((entries) => {
    inView = entries.at(-1)?.isIntersecting ?? true;
    sync();
  });
  observer?.observe(el);
  const onVisibility = () => { visible = document.visibilityState === 'visible'; sync(); };
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    observer?.disconnect();
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

export function useOrbAnimator(
  hostRef: RefObject<HTMLElement | null>,
  { state, levelRef, speed = 1, onFrame }: { state: OrbState; levelRef?: RefObject<number>; speed?: number; onFrame: (frame: OrbFrame) => void },
) {
  const optionsRef = useRef({ state, levelRef, speed, onFrame });
  const wakeRef = useRef<() => void>(() => {});

  useEffect(() => {
    optionsRef.current = { state, levelRef, speed, onFrame };
    wakeRef.current();
  }, [state, levelRef, speed, onFrame]);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const mix = createStateMix(optionsRef.current.state);
    const frame: OrbFrame = { dt: 0, phase: 0, weights: mix.weights, level: 0, reduced: matchMedia(REDUCED_MOTION).matches };
    let raf = 0;
    let last: number | null = null;
    let active = true;

    const tick = (now: number) => {
      raf = 0;
      const dt = last === null ? 0 : Math.min((now - last) / 1000, MAX_FRAME_DT);
      last = now;
      const opts = optionsRef.current;
      const live = opts.levelRef?.current;
      mix.update(opts.state, dt);
      if (!frame.reduced) frame.phase += dt * Math.max(0, opts.speed);
      const target = frame.reduced ? 0 : typeof live === 'number' && live >= 0 ? live : blendEnergy(mix.weights, frame.phase);
      frame.level = smoothLevel(frame.level, target, dt);
      frame.dt = dt;
      opts.onFrame(frame);
      const settled = frame.reduced && mix.weights[opts.state] > 0.999 && frame.level < 0.001;
      if (active && !settled) raf = requestAnimationFrame(tick);
      else last = null;
    };
    const wake = () => { if (active && raf === 0) raf = requestAnimationFrame(tick); };
    const halt = () => {
      if (raf !== 0) cancelAnimationFrame(raf);
      raf = 0;
      last = null;
    };
    wakeRef.current = wake;

    const unobserve = observeActivity(el, (next) => {
      active = next;
      if (next) wake();
      else halt();
    });
    const motion = matchMedia(REDUCED_MOTION);
    const onMotion = () => { frame.reduced = motion.matches; wake(); };
    motion.addEventListener('change', onMotion);
    wake();
    return () => {
      halt();
      unobserve();
      motion.removeEventListener('change', onMotion);
      wakeRef.current = () => {};
    };
  }, [hostRef]);
}
